import RemoveLayerConfirmationModal from 'samples/plate-maps/display-layer-controls/RemoveLayerConfirmationModal';
import {PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {anyWellsHaveChangedTagForSchemaField} from 'samples/plate-maps/PlateMapUtil';
import runModal from 'ui-library/runModal';

/**
 * Wrapper around a function to delete a well schema field from a plate map. Launches a confirmation modal if
 * there are any non-default tags set for the schema field in the plate map already.
 * @param plateMap The current state of the plate map
 * @param initialPlateMap The plate of the plate map before the user started editing it
 * @param schemaFieldId The id of the schema field to maybe be deleted
 * @param onConfirmDelete A function to be called when the user confirms they want to delete the schema field.
 * @param onLaunchModal A function to be called immediately before the confirmation modal opens.
 */
export function confirmDeleteSchemaField({
  plateMap,
  initialPlateMap,
  schemaFieldId,
  onConfirmDelete,
  onLaunchModal,
}: {
  plateMap: PlateMap;
  initialPlateMap: PlateMap;
  schemaFieldId: string;
  /**
   * A function to be called when the user submits the confirmation modal, or immediately if no confirmation
   * modal is needed.
   */
  onConfirmDelete: (id: string) => void;
  /** A function to be called when the confirmation modal launches. */
  onLaunchModal?: () => void;
}): void {
  if (anyWellsHaveChangedTagForSchemaField(plateMap, initialPlateMap, schemaFieldId)) {
    onLaunchModal?.();
    runModal(RemoveLayerConfirmationModal, {
      layerNameText: 'schema field',
      onRequestSubmit: () => {
        onConfirmDelete(schemaFieldId);
      },
    });
  } else {
    onConfirmDelete(schemaFieldId);
  }
}

export function toggleSchemaField({
  newSelectedIds,
  oldSelectedIds,
  plateMap,
  initialPlateMap,
  onConfirmAdd,
  onConfirmDelete,
  onLaunchConfirmDeleteModal,
}: {
  newSelectedIds: Array<string>;
  oldSelectedIds: Set<string>;
  plateMap: PlateMap;
  initialPlateMap: PlateMap;
  onConfirmAdd: (idToAdd: string) => void;
  onConfirmDelete: (idToDelete: string) => void;
  onLaunchConfirmDeleteModal: () => void;
}): void {
  // Values are selected or deselected one at a time. Therefore, we're only trying to delete a schema
  // field if newSelectedIds is smaller than oldSelectedIds.
  if (newSelectedIds.length < oldSelectedIds.size) {
    const newIdSet = new Set(newSelectedIds);
    const [schemaFieldId] = [...oldSelectedIds].filter((id) => !newIdSet.has(id));
    confirmDeleteSchemaField({
      plateMap,
      initialPlateMap,
      schemaFieldId,
      onConfirmDelete,
      onLaunchModal: onLaunchConfirmDeleteModal,
    });
  } else {
    const [newId] = newSelectedIds.filter((id) => !oldSelectedIds.has(id));
    onConfirmAdd(newId);
  }
}
